import { useCallback, useEffect, useState, type FormEvent } from "react";
import type { CategoryWithServices, DiscountRule } from "@shared/modules/x-atelier";
import { Badge } from "../../../core/components/ui/badge";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Field, Input } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { useToastStore } from "../../../core/stores/toast";
import {
  createDiscountRule,
  deleteDiscountRule,
  listDiscountRules,
  updateDiscountRule,
} from "../lib/api";

const FORM_ID = "atelier-discount-rule-form";

interface Draft {
  name: string;
  percent: number;
  triggerServiceIds: string[];
  targetServiceIds: string[];
  active: boolean;
}

const emptyDraft = (): Draft => ({
  name: "",
  percent: 30,
  triggerServiceIds: [],
  targetServiceIds: [],
  active: true,
});

/** Cases à cocher des services, par catégorie, pour les déclencheurs ou les cibles. */
function ServicePicker({
  categories,
  value,
  onChange,
  label,
}: {
  categories: CategoryWithServices[];
  value: string[];
  onChange: (ids: string[]) => void;
  label: string;
}) {
  const toggle = (id: string, checked: boolean) =>
    onChange(checked ? [...value, id] : value.filter((v) => v !== id));
  return (
    <fieldset className="rule-picker">
      <legend className="rule-picker__legend">{label}</legend>
      {categories.map((c) => (
        <div key={c.id} className="rule-picker__group">
          <span className="rule-picker__category">{c.name}</span>
          {c.services
            .filter((s) => s.active)
            .map((s) => (
              <label key={s.id} className="atelier-check rule-picker__item">
                <input
                  type="checkbox"
                  checked={value.includes(s.id)}
                  onChange={(e) => toggle(s.id, e.target.checked)}
                />
                {s.name}
              </label>
            ))}
        </div>
      ))}
    </fieldset>
  );
}

/**
 * Rabais automatiques : « quand l'un de ces services est au rendez-vous, ceux-là
 * sont à −N % ». Lisibles par toute l'équipe (on sait ce qui se déclenche),
 * modifiables par les administrateurs. Appliqués par l'API à chaque rendez-vous.
 */
export function DiscountRulesCard({
  categories,
  isAdmin,
}: {
  categories: CategoryWithServices[];
  isAdmin: boolean;
}) {
  const push = useToastStore((s) => s.push);
  const [rules, setRules] = useState<DiscountRule[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [modal, setModal] = useState<{ open: boolean; rule?: DiscountRule }>({ open: false });
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [confirm, setConfirm] = useState<DiscountRule | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    listDiscountRules()
      .then(setRules)
      .catch(() => setRules([]))
      .finally(() => setLoaded(true));
  }, []);

  useEffect(load, [load]);

  const nameOf = (id: string) =>
    categories.flatMap((c) => c.services).find((s) => s.id === id)?.name ?? "service retiré";

  const open = (rule?: DiscountRule) => {
    setDraft(
      rule
        ? {
            name: rule.name,
            percent: rule.percent,
            triggerServiceIds: rule.triggerServiceIds,
            targetServiceIds: rule.targetServiceIds,
            active: rule.active,
          }
        : emptyDraft(),
    );
    setModal({ open: true, rule });
  };

  const valid =
    draft.name.trim().length > 0 &&
    draft.percent >= 1 &&
    draft.percent <= 100 &&
    draft.triggerServiceIds.length > 0 &&
    draft.targetServiceIds.length > 0;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    try {
      const input = { ...draft, name: draft.name.trim(), percent: Math.round(draft.percent) };
      if (modal.rule) await updateDiscountRule(modal.rule.id, input);
      else await createDiscountRule(input);
      push(
        "success",
        modal.rule ? "Règle mise à jour" : "Règle créée",
        `−${input.percent} % appliqués automatiquement.`,
      );
      setModal({ open: false });
      load();
    } catch {
      push("danger", "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (rule: DiscountRule) => {
    if (busy) return;
    setBusy(true);
    try {
      await updateDiscountRule(rule.id, { active: !rule.active });
      load();
    } catch {
      push("danger", "Modification impossible");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirm || busy) return;
    setBusy(true);
    try {
      await deleteDiscountRule(confirm.id);
      push("success", "Règle supprimée");
      setConfirm(null);
      load();
    } catch {
      push("danger", "Suppression impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card rules">
      <div className="rules__head">
        <div>
          <h2 className="rules__title">Rabais automatiques</h2>
          <p className="rules__sub">
            Quand un service déclencheur est au rendez-vous, les services cibles se remisent tout
            seuls.
          </p>
        </div>
        {isAdmin ? (
          <Button variant="ghost" icon={<Icon name="plus" size={16} />} onClick={() => open()}>
            Nouvelle règle
          </Button>
        ) : null}
      </div>

      {!loaded ? (
        <p className="rules__empty">Chargement…</p>
      ) : rules.length === 0 ? (
        <p className="rules__empty">
          Aucune règle.{" "}
          {isAdmin ? "Exemple : « Antirouille → protection extérieure à −30 % »." : ""}
        </p>
      ) : (
        <ul className="rules__list">
          {rules.map((r) => (
            <li key={r.id} className={`rule${r.active ? "" : " rule--off"}`}>
              <span className="rule__pct">−{r.percent} %</span>
              <span className="rule__text">
                <span className="rule__name">{r.name}</span>
                <span className="rule__sentence">
                  Avec {r.triggerServiceIds.map(nameOf).join(" ou ")} →{" "}
                  {r.targetServiceIds.map(nameOf).join(", ")}
                </span>
              </span>
              <span className="rule__state">
                {r.active ? (
                  <Badge variant="success">Active</Badge>
                ) : (
                  <Badge variant="neutral">Inactive</Badge>
                )}
              </span>
              {isAdmin ? (
                <span className="rule__actions">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => void toggleActive(r)}
                  >
                    {r.active ? "Désactiver" : "Activer"}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => open(r)}>
                    Modifier
                  </Button>
                  <Button variant="ghost-danger" size="sm" onClick={() => setConfirm(r)}>
                    Supprimer
                  </Button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={modal.open}
        title={modal.rule ? "Modifier la règle" : "Nouvelle règle de rabais"}
        onClose={() => setModal({ open: false })}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setModal({ open: false })} disabled={busy}>
              Annuler
            </Button>
            <Button type="submit" form={FORM_ID} variant="primary" disabled={busy || !valid}>
              {modal.rule ? "Enregistrer" : "Créer la règle"}
            </Button>
          </>
        }
      >
        <form id={FORM_ID} className="atelier-form" onSubmit={(e) => void save(e)}>
          <div className="form-section__grid">
            <Field label="Nom" hint="Apparaît sur la ligne remisée et sur la facture.">
              <Input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="ex : Antirouille + protection extérieure"
                autoFocus
                required
              />
            </Field>
            <Field label="Rabais (%)" hint="100 = offert.">
              <Input
                type="number"
                min={1}
                max={100}
                value={draft.percent}
                onChange={(e) => setDraft({ ...draft, percent: Number(e.target.value) })}
              />
            </Field>
          </div>
          <div className="rule-pickers">
            <ServicePicker
              categories={categories}
              label="Quand l'un de ces services est au rendez-vous…"
              value={draft.triggerServiceIds}
              onChange={(ids) => setDraft({ ...draft, triggerServiceIds: ids })}
            />
            <ServicePicker
              categories={categories}
              label="…ces services sont remisés"
              value={draft.targetServiceIds}
              onChange={(ids) => setDraft({ ...draft, targetServiceIds: ids })}
            />
          </div>
          <label className="atelier-check">
            <input
              type="checkbox"
              checked={draft.active}
              onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
            />
            Active
          </label>
        </form>
      </Modal>

      <Modal
        open={confirm !== null}
        title="Supprimer cette règle ?"
        onClose={() => setConfirm(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              Annuler
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => void remove()}>
              Supprimer
            </Button>
          </>
        }
      >
        <p>
          « {confirm?.name} » ne s'appliquera plus aux prochains rendez-vous ; les rabais déjà
          accordés restent.
        </p>
      </Modal>
    </section>
  );
}
